import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetBarcodeRanges } from '../../../store/masterData/Actions';
import { formatDateTime, upsertArrayElement } from '../../../services/appFunctionsService';
import BarcodeRangeHttpService from '../../../services/http/masterData/barcodeRangeHttpService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import Screen from '../../../components/Screen';
import BarcodeRangeForm from './form/BarcodeRangeForm';
import { ConfirmationDialog, CustomTable, ProjectDialog } from '@zz2/zz2-ui';
import { IApiError } from '../../../@types/other';
import { setBarcodeRangeMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { IBarcodeRange } from '../../../@types/model/masterData/barcodeRange/barcodeRange';
import { IBarcodeRangeFormValues } from '../../../@types/model/masterData/barcodeRange/barcodeRangeFormValues';
import BarcodeRangeModelHelper from '../../../@types/model/masterData/barcodeRange/barcodeRangeModelHelper';
import { useLocation } from 'react-router-dom';
import UserHelper from '../../../services/helper/userHelper';
import { RightGrantsEnum } from '../../../@types/enum/rightGrantsEnum';

const BarcodeRangeScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const location = useLocation();
    const currentUser = useAppSelector(x => x.auth.session?.user ?? null);
    const barcodeRanges = useAppSelector(x => x.masterData.barcodeRanges);
    const sites = useAppSelector(x => x.masterData.sites);

    const [isLoading, setIsLoading] = useState(false);
    const [selectedBarcodeRange, setSelectedBarcodeRange] = useState<IBarcodeRange | null>(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [deletingBarcodeRange, setDeletingBarcodeRange] = useState<IBarcodeRange | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        fetchData();
    }, []);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    const fetchData = async () => {
        const isIndexedDBAvailable = !!self.indexedDB;
        if (isIndexedDBAvailable) {
            await syncMasterData(false);
        } else {
            setIsLoading(true);
            try {
                const res = await BarcodeRangeHttpService.getBarcodeRangeData();
                dispatch(dataSetBarcodeRanges(res.data));
            } catch (error : unknown) {
                const apiError = error as IApiError | undefined;
                generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading barcode ranges.');
            } finally {
                setIsLoading(false);
            }
        }
    };

    const onSubmit = async (values : IBarcodeRangeFormValues) => {
        setIsLoading(true);

        try {
            const upsert = BarcodeRangeModelHelper.createUpsert(values);
            const res = await BarcodeRangeHttpService.addOrUpdateBarcodeRange(upsert);
            const updatedList = upsertArrayElement(barcodeRanges, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetBarcodeRanges(updatedList));
            await setBarcodeRangeMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedBarcodeRange ? 'Barcode range updated successfully.' : 'Barcode range added successfully.');
            onModalDismiss();
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while updating barcode range.');
        } finally {
            setIsLoading(false);
        }
    };

    const removeBarcodeRange = async () => {
        if (!deletingBarcodeRange) return;

        setIsLoading(true);
        try {
            const res = await BarcodeRangeHttpService.deleteBarcodeRange(deletingBarcodeRange.id);
            const updatedList = upsertArrayElement(barcodeRanges, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetBarcodeRanges(updatedList));
            await setBarcodeRangeMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Barcode range deleted successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError | undefined;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting barcode range.');
        } finally {
            closeDeletePopup();
            setIsLoading(false);
        }
    };

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const openModal = () => {
        setIsDialogOpen(true);
    };

    const onModalDismiss = () => {
        setIsDialogOpen(false);
        setSelectedBarcodeRange(null);
    };

    const edit = (selectedRow : IBarcodeRange) => {
        setSelectedBarcodeRange(selectedRow);
        openModal();
    };

    const onDelete = (selectedRow : IBarcodeRange) : void => {
        setDeletingBarcodeRange(selectedRow);
    };

    const closeDeletePopup = () : void => {
        setDeletingBarcodeRange(null);
    };

    const deleteButtonTooltip = (row : IBarcodeRange) : string => {
        let message = 'Delete';

        if (!userHasRights.deleteRight) {
            message = UserHelper.getRelatedRightToolTipMessage('Barcode Range', RightGrantsEnum.Delete);
        } else if (!row.isActive) {
            message = 'Already Inactive.';
        }

        return message;
    };

    const formatSites = useCallback((siteIds : Array<number>) => sites
        .filter(x => siteIds.some(y => y === x.id))
        .map(x => x.shortDescription).toString().replace(/,/g, ', '), [sites]);
    
    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return BarcodeRangeModelHelper.createFormValues(selectedBarcodeRange, sites);
        }
    }, [selectedBarcodeRange, isDialogOpen]);

    const userHasRights = useMemo(() => {
        return UserHelper.userRelatedRights(location.pathname, currentUser?.rights);
    }, [currentUser, location]);

    /*================================================================================================================
    *                                                  Render Methods
    * ==============================================================================================================*/

    return (
        <Screen isLoading={isLoading}>
            <Card className={'fdc hfill'}>
                <CustomTable<IBarcodeRange>

                    enableAdding
                    // addTooltipRightValidation={!userHasRights.addRight}
                    disableAddButton={!userHasRights.addRight || isLoading}
                    addTooltip={!userHasRights.addRight ? UserHelper.getRelatedRightToolTipMessage('Barcode Range', RightGrantsEnum.Add) : 'Add'}
                    addFunction={openModal}
                    // addTooltipOnClick={() : void => requestAccess('Add')}
                
                    enableEditing
                    // editTooltipRightValidation={!userHasRights.editRight}
                    disableEditButton={!userHasRights.editRight || isLoading}
                    editTooltip={!userHasRights.editRight ? UserHelper.getRelatedRightToolTipMessage('Barcode Range', RightGrantsEnum.Edit) : 'Edit'}
                    editFunction={edit}
                    // editTooltipOnClick={() : void => requestAccess('Edit')}

                    enableDeleting
                    // deleteTooltipRightValidation={!userHasRights.deleteRight}
                    disableDeleteButton={(row : IBarcodeRange) : boolean => !userHasRights.deleteRight || !row.isActive || isLoading}
                    deleteTooltip={deleteButtonTooltip}
                    deleteFunction={onDelete}
                    // deleteTooltipOnClick={() : void => requestAccess('Delete')}

                    enableSorting
                    enableFiltering
                    enablePagination
                    fitWidthToPage
                    columns={[
                        { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                        { title: 'Indicator Digit', field: 'indicatorDigit', enableFiltering: true, enableSorting: true },
                        { title: 'Range Start', field: 'rangeStart', enableFiltering: true, enableSorting: true },
                        { title: 'Range End', field: 'rangeEnd', enableFiltering: true, enableSorting: true },
                        { title: 'Latest', field: 'latest', enableFiltering: true, enableSorting: true },
                        { title: 'Import Code', field: 'importCode', enableFiltering: true, enableSorting: true },
                        { title: 'Amount Remaining', field: 'numRemaining', enableFiltering: true, enableSorting: true },
                        { title: 'Export?', field: 'isExport', type: 'boolean', enableFiltering: true, enableSorting: true },
                        { title: 'Sites', field: 'siteIds', formatFunction: formatSites, width: 200, enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={barcodeRanges}
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={190}
                    isActive={row => row.isActive}
                />
            </Card>
            {isDialogOpen && initialValues &&
                <ProjectDialog
                    title={selectedBarcodeRange ? 'Edit Barcode Range' : 'Add Barcode Range'}
                    isLoadingCircular={isLoading}
                    isOpen={isDialogOpen}
                    onClose={onModalDismiss}
                    maxWidth={'sm'}
                    fullWidth
                >
                    <BarcodeRangeForm
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={onModalDismiss}
                        isLoading={isLoading}
                    />
                </ProjectDialog>
            }
            <ConfirmationDialog
                title={'Inactivate Barcode Range'}
                open={!!deletingBarcodeRange}
                description={'Are you sure you would like to set the following item to inactive?'}
                onAccept={removeBarcodeRange}
                onClose={closeDeletePopup}
                dialogType={'red'}
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default BarcodeRangeScreen;
